import {DataFetcher} from '@aboutyou/data-fetcher';
import {DiscountCardInformationSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/discount_card_information_section.types';
import {
  GetDiscountsResponse,
  GetDiscountsResponse_Discount,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/campaigns/v1/service.types';
import {useHistory} from '@aboutyou/router';
import {useTrackFeature} from '@aboutyou/tracking';
import {DiscountCard} from '@aboutyou/ui-components';
import React, {useMemo} from 'react';
import {useIsSelectedDiscountAppOnly} from '../../backend-services/Campaigns/SelectedDiscounts/useSelectedDiscount';
import {useSelectedDiscountColor} from '../../backend-services/Campaigns/SelectedDiscounts/useSelectedDiscountColor';
import {getTrackingDiscountType} from '../../backend-services/Campaigns/getTrackingDiscountType';
import {
  useDiscounts,
  useDiscountsRequest,
} from '../../backend-services/Campaigns/hooks/useDiscounts';
import {useIsUrgencyTriggersDisabled} from '../../shared-deprecated/useIsUrgencyTriggersDisabled';
import {DiscountCardThumbnails} from './DiscountCardThumbnails';
import {useMappedDiscountsCallback} from './hooks/useMappedDiscounts';

interface DiscountProps {
  className: string | undefined;
  getMappedDiscounts: ({
    discount,
  }: {
    discount?: GetDiscountsResponse_Discount;
  }) =>
    | {
        discountId: string;
        backgroundColor: string;
        headline: string;
        subline: string | undefined;
        validUntil: Date;
        conditionText: string | undefined;
        'data-testid': string;
        savingsLabel: string | undefined;
        showCountdown: boolean;
      }
    | undefined;
  onCardClick: (discount?: GetDiscountsResponse_Discount) => void;
  cardThumbnails: React.JSX.Element | undefined;
  hideCountdown: boolean;
}

type DataFetcherProps = {
  View: React.ComponentType<GetDiscountsResponse & Omit<DiscountProps, 'ref'>>;
  discount?: GetDiscountsResponse_Discount; // Add the discount property to the type
} & DiscountProps;

const ClientOnlyDataComponent = ({
  getMappedDiscounts,
  className,
  onCardClick,
  View,
  cardThumbnails = undefined,
  hideCountdown,
}: DataFetcherProps) => {
  const request = useDiscounts();
  return (
    <DataFetcher
      View={View}
      Error={null}
      viewProps={{
        getMappedDiscounts,
        className,
        onCardClick,
        cardThumbnails,
        hideCountdown,
      }}
      request={request}
    />
  );
};

const ServerDataIncludedComponent = ({
  getMappedDiscounts,
  className,
  onCardClick,
  View,
  cardThumbnails = undefined,
  hideCountdown,
}: DataFetcherProps) => {
  const request = useDiscountsRequest();
  return (
    <DataFetcher
      View={View}
      Error={null}
      viewProps={{
        getMappedDiscounts,
        className,
        onCardClick,
        cardThumbnails,
        hideCountdown,
      }}
      request={request}
    />
  );
};

const ViewComponent: React.ComponentType<
  GetDiscountsResponse & Omit<DiscountProps, 'ref'>
> = ({
  getMappedDiscounts,
  className,
  onCardClick,
  cardThumbnails = undefined,
  hideCountdown,
  ...props
}) => {
  if (!props.discount) {
    return null;
  }

  const mappedDiscount = getMappedDiscounts({
    discount: props.discount,
  });

  return (
    <DiscountCard
      {...mappedDiscount!}
      onClick={() => {
        onCardClick(props.discount);
      }}
      DiscountCardThumbnails={cardThumbnails}
      className={className}
      hideCountdown={hideCountdown}
    />
  );
};

export const DiscountCardWithData = ({
  className,
  discountCardThumbnails,
  clientOnlyData = false,
}: {
  className?: string;
  discountCardThumbnails?: DiscountCardInformationSection;
  clientOnlyData?: boolean;
}) => {
  const getMappedDiscounts = useMappedDiscountsCallback();
  const selectedDiscountColor = useSelectedDiscountColor();
  const isAppOnlyDiscount = useIsSelectedDiscountAppOnly();
  const trackFeature = useTrackFeature();
  const history = useHistory();
  const hideCountdown = useIsUrgencyTriggersDisabled();

  const onCardClick = (discount?: GetDiscountsResponse_Discount) => {
    if (!discount) {
      return;
    }
    trackFeature({
      eventType: 'click',
      label: getTrackingDiscountType(discount),
      name: getTrackingDiscountType(discount),
      value: discount.id,
    });

    if (discount.linkTarget) {
      history.push(discount.linkTarget.url);
    }
  };

  const cardThumbnails = useMemo(() => {
    if (discountCardThumbnails?.type?.$case === 'discountBannerProducts') {
      const discountBannerProducts =
        discountCardThumbnails.type.discountBannerProducts;

      return (
        <DiscountCardThumbnails
          color={selectedDiscountColor}
          discountBannerProducts={discountBannerProducts}
        />
      );
    }

    return undefined;
  }, [discountCardThumbnails, selectedDiscountColor]);

  if (isAppOnlyDiscount) {
    return null;
  }

  return clientOnlyData ? (
    <ClientOnlyDataComponent
      getMappedDiscounts={getMappedDiscounts}
      className={className}
      onCardClick={onCardClick}
      cardThumbnails={cardThumbnails}
      View={ViewComponent}
      hideCountdown={hideCountdown}
    />
  ) : (
    <ServerDataIncludedComponent
      getMappedDiscounts={getMappedDiscounts}
      className={className}
      onCardClick={onCardClick}
      cardThumbnails={cardThumbnails}
      View={ViewComponent}
      hideCountdown={hideCountdown}
    />
  );
};
